<template>
    <DropdownComp
        ref="dropdownCompRef"
        :translationkey="selectedText"
        :icon="selectedIcon"
        :show-arrow="showArrow"
        :columns="columns"
        :auto-close="isMultiSelect ? 'outside' : undefined"
        :dropdown-items="dropdownItems"
        :disabled="props.disabled"
        :loadstate="props.loadstate"
        :show-item-count-badge="showItemCountBadge"
        :virtual-scroller-max-items="virtualScrollerMaxItems"
        :classes="classes"
        :markValues="selectedDropdownItems"
        :autoOpen="autoOpen"
    />
</template>
<script setup lang="ts">
/**
 * This is a wrapper for a DropdownComponent that is used to select a single item from a list of items. With a searchfield in the dropdown.
 * The selected item is displayed in the dropdown button
 */
import { DropdownComp } from '@/components';
import {
    DropdownItemButton,
    DropdownItemCheckbox,
    DropdownItemGroup,
    DropdownItemSeparator,
    DropdownItemTextInput,
    type DropdownItemTypes,
    type SelectLine,
    type SelectLineValueType,
} from '@/objects';
import { computed, ref } from 'vue';
import { CreateRegexpSearchPatternForSearchType } from '@/utilities';
import { SelectLineGroup } from '@/objects/SelectLineGroup';
import { $tm } from '@/locize';
import type { TaskLoaderState } from '@/composeables';
import type { SearchType } from '@/objects/generated';

const props = withDefaults(
    defineProps<{
        /**
         * The data to display in the dropdown
         * This can be a single list of @type SelectLine or a list of @type SelectLineGroup
         */
        data: SelectLine[] | SelectLineGroup[] | Map<SelectLineValueType, SelectLine>;

        /**
         * The caption of the dropdown
         * This is the initial text that is displayed in the dropdown, if no value is selected we preselect the first item in the list
         * @default null
         */
        caption?: string | null;

        /**
         * The icon of the dropdown
         * This is the initial icon that is displayed in the dropdown, if no value is selected we preselect the first item in the list
         * @default null
         */
        icon?: string | null;

        /**
         * If the searchfield should be displayed in the dropdown
         * @default false
         */
        showSearch?: boolean;

        /**
         * If the arrow should be displayed in the dropdown
         * @default true
         */
        showArrow?: boolean;

        /**
         * Wheter the caption should be kept when an item is selected or not
         * @default false (caption is replaced by the selected item)
         */
        keepCaption?: boolean;

        /**
         * If we want check all buttons on the header of the dropdown. Then we can select all elements in a group
         * Note, this is only applicable if we have a mulitselect dropdown
         */
        checkAll?: boolean;

        /**
         * The number of columns to display in the dropdown. This should only be used on dropdowns that we know will have a lot of items
         * @default undefined (no columns)
         */
        columns?: 1 | 2 | 3 | 4 | 5;

        /**
         * The favorites that should be displayed at the top of the list always
         */
        favorites?: Array<SelectLineValueType>;

        /**
         * If the input is disabled
         * @default undefined
         */
        disabled?: boolean;

        /**
         * This will show a loader in the button and disable the button
         * @default null
         */
        loadstate?: TaskLoaderState | null;

        /**
         * Specifies if we should show a badge with the number of items in the dropdown
         * @default false
         */
        showItemCountBadge?: boolean;

        /**
         * The search type to use for filtering the items in the dropdown
         */
        searchType?: SearchType;

        /**
         * The maximum items to render at any given time
         * If this is set the dropdown uses a virtual scroller to only render some of the items.
         * A reasonable number to put here if you have alot of items is 25
         */
        virtualScrollerMaxItems?: number;

        /**
         * Internal classes to apply to the dropdown wrapper
         */
        classes?: string;

        /**
         * If true, the dropdown will automatically open when rendered
         */
        autoOpen?: boolean;
    }>(),
    {
        showSearch: false,
        icon: null,
        caption: null,
        showArrow: true,
        keepCaption: false,
        columns: undefined,
        showItemCountBadge: false,
        searchType: 'Contains',
    },
);

const modelValue = defineModel<SelectLineValueType | SelectLineValueType[]>({ required: true });
const dropdownCompRef = ref<InstanceType<typeof DropdownComp> | null>(null);

defineExpose({
    focus: () => {
        dropdownCompRef.value?.focus();
    },
});

/**
 * The current searchstring from the searchfield
 */
const searchText = ref<string>('');

const dataWithoutMap = computed(() => {
    if (props.data instanceof Map) {
        return Array.from(props.data.values());
    }

    return props.data;
});

/**
 * We need to map the selectlines to dropdown items so we can set what item comes from what selectline
 */
const mappedDropdownItems = computed(
    (): {
        allDropDownItems: DropdownItemTypes[];
        selectLineToDropdownItems: Map<SelectLine, DropdownItemTypes[]>;
    } => {
        // Copy the data so we dont mutate the original array
        const selectLines = [...dataWithoutMap.value];

        // Sort so we get the favorites first, in the favorites order
        selectLines.sort((a, b) => {
            // Both are groups. Groups cant be favorites so they are not resorted.
            if (a instanceof SelectLineGroup && b instanceof SelectLineGroup) {
                return 0;
            }

            // Both are favorites. Sort them in their relative order in the favorites array
            if (
                !(a instanceof SelectLineGroup) &&
                !(b instanceof SelectLineGroup) &&
                props.favorites?.includes(a.value) &&
                props.favorites?.includes(b.value)
            ) {
                return props.favorites.indexOf(a.value) - props.favorites.indexOf(b.value);
            }

            // a is a favorite, b is not. a comes first
            if (!(a instanceof SelectLineGroup) && props.favorites?.includes(a.value)) {
                return -1;
            }

            // b is a favorite, a is not. b comes first
            if (!(b instanceof SelectLineGroup) && props.favorites?.includes(b.value)) {
                return 1;
            }

            // Any other case we dont move the item
            return 0;
        });

        // Map selectline to dropdown items. Its itemS because we might add a separator.
        // TODO: Remove the separator bool from SelectLine
        // TODO: Remove SelectLine(?). Or atleast fix it to be generic
        const createDropdownItemsForSelectLine = (line: SelectLine): DropdownItemTypes[] => {
            const hideDropDownItem = computed(() => {
                if (searchPattern.value == undefined) {
                    return false;
                }

                // Note: We are making new RegExp objects here since a regexp will remember its state when we have the /g flag
                // We only have one searchPattern, if we use that to test we will move the internal `lastIndex` of the regexp, and miss matches

                if (new RegExp(searchPattern.value).test(line.text)) {
                    // We match the text, dont hide the item
                    return false;
                }

                if (line.subtext && line.subtext.length) {
                    if (new RegExp(searchPattern.value).test(line.subtext)) {
                        // We match the subtext, dont hide the item
                        return false;
                    }
                }

                // We dont match the search, so we hide the item
                return true;
            });
            return [
                isMultiSelect
                    ? DropdownItemCheckbox.create({
                          // This is kinda nasty.
                          // We should merge InputBool and InputCheckbox and write them so they support arrays the same way as a html checkbox does
                          modelValue: computed({
                              get() {
                                  if (modelValue.value instanceof Array) {
                                      return modelValue.value.includes(line.value);
                                  }
                                  throw new Error('We should never get here if the modelValue is not an array');
                              },
                              set(value) {
                                  if (!(modelValue.value instanceof Array)) {
                                      throw new Error('We should never get here if the modelValue is not an array');
                                  }

                                  // If we check the checkbox, add the linevalue the modelValue (if its not already there)
                                  if (value) {
                                      if (!modelValue.value.includes(line.value)) {
                                          modelValue.value = [...modelValue.value, line.value];
                                      }
                                  }
                                  // If we uncheck the checkbox, remove the linevalue from the modelValue
                                  else {
                                      modelValue.value = modelValue.value.filter((v) => v !== line.value);
                                  }
                              },
                          }),
                          label: line.text,
                          subtext: line.subtext,
                          markTextPart: searchPattern.value, // TODO: This is using the markTextPart from the InputBool props. The DropdownItemButton is using the one defined in the DropdownItemBase. This should be consistent.
                          hideDropDownItem: hideDropDownItem,
                      })
                    : DropdownItemButton.create({
                          translationkey: line.text,
                          markTextPart: searchPattern,
                          icon: line.icon,
                          disabled: line.disabled,
                          subtext: line.subtext,
                          subtextclass: line.subtextclass,
                          hideDropDownItem: hideDropDownItem,
                          uniqueLoopKey: `${line.value}-${line.text}-${line.subtext}-${line.icon}`,
                          action: () => {
                              modelValue.value = line.value;
                          },
                      }),
                // TODO: The separator should not be a property of the line in my opinion. This should be removed so its easier to use
                line.separator ? DropdownItemSeparator.create() : undefined,
            ].filter((item) => item !== undefined);
        };

        const selectLineToDropdownItems = new Map<SelectLine, DropdownItemTypes[]>();

        selectLines.forEach((item) => {
            if (item instanceof SelectLineGroup) {
                item.lines.forEach((line) => {
                    selectLineToDropdownItems.set(line, createDropdownItemsForSelectLine(line));
                });
                return;
            }

            selectLineToDropdownItems.set(item, createDropdownItemsForSelectLine(item));
        });

        // Map selectlinegroup to dropdown items
        const selectLineGroupToDropdownItems = (group: SelectLineGroup): DropdownItemTypes[] => {
            const groupChildren = group.lines.flatMap((line) => selectLineToDropdownItems.get(line) ?? []);
            // Hide the group header if all items in the group are hidden
            const wholeGroupIsHidden = computed(() =>
                groupChildren.every((item) => item.hideDropDownItem === undefined || item.hideDropDownItem.value),
            );

            return [
                DropdownItemGroup.create({
                    items: groupChildren,
                    hideDropDownItem: wholeGroupIsHidden,
                    title: $tm(group.title),
                    showSeparator: true,
                }),
            ];
        };

        const favoritesActualLength = selectLines.filter(
            (item) => !(item instanceof SelectLineGroup) && props.favorites?.includes(item.value),
        ).length;

        const favoriteDropDownItems = selectLines
            .slice(0, favoritesActualLength)
            .flatMap((item) =>
                item instanceof SelectLineGroup ? selectLineGroupToDropdownItems(item) : (selectLineToDropdownItems.get(item) ?? []),
            );

        const restDropDownItems = selectLines
            .slice(favoritesActualLength)
            .flatMap((item) =>
                item instanceof SelectLineGroup ? selectLineGroupToDropdownItems(item) : (selectLineToDropdownItems.get(item) ?? []),
            );

        const allDropDownItems = [
            // Favorites (these will never be a SelectLineGroup, but we check anyway to make the compiler happy)
            ...favoriteDropDownItems,

            // Separator between favorites and the rest of the items
            DropdownItemSeparator.create({
                // The separator should be visible if any of the favorites are visible
                hideDropDownItem: computed((): boolean =>
                    favoriteDropDownItems.every(
                        (item: DropdownItemTypes) => item.hideDropDownItem === undefined || item.hideDropDownItem.value,
                    ),
                ),
            }),

            // Rest of the items
            ...restDropDownItems,
        ];

        return {
            allDropDownItems,
            selectLineToDropdownItems,
        };
    },
);

/**
 * All the dropdown items mapped from the selectlines (without the searchbox and the separator)
 */
const allDropDownItems = computed(() => mappedDropdownItems.value.allDropDownItems);

const searchPattern = computed(() => {
    if (!searchText.value) {
        return undefined;
    }
    return CreateRegexpSearchPatternForSearchType(searchText.value, props.searchType);
});

/**
 * The actual dropdownItems we will display in the dropdown
 */
const dropdownItems = computed(() => {
    return [
        // Only show the search if showSearch is true
        ...(!props.showSearch
            ? []
            : [
                  // Search field
                  DropdownItemTextInput.create({
                      placeholder: 'general.search',
                      modelValue: searchText,
                  }),
                  // Separator between searchfield and options
                  DropdownItemSeparator.create(),
              ]),
        // Options
        ...allDropDownItems.value,
    ];
});

// Check if we can select multiple items
// We use this one to render the checkboxes
const isMultiSelect = modelValue.value instanceof Array;

/**
 * What item is selected
 */
const selectedItem = computed(
    () =>
        (dataWithoutMap.value.find((item) => !(item instanceof SelectLineGroup) && item.value === modelValue.value) as
            | SelectLine
            | undefined) ??
        dataWithoutMap.value
            .filter((item) => item instanceof SelectLineGroup)
            .flatMap((group) => group.lines)
            .find((item) => item.value === modelValue.value),
);

/**
 * what dropdown item(s) is selected. This is used to set the active state in the dropdown and to show the checkboxes as checked
 */
const selectedDropdownItems = computed(() => {
    if (!selectedItem.value) {
        return [];
    }

    const itemsFromSelectedLine = mappedDropdownItems.value.selectLineToDropdownItems.get(selectedItem.value) ?? [];
    const selectedDropdownItem = itemsFromSelectedLine.find((item) => !(item instanceof DropdownItemSeparator));

    if (!selectedDropdownItem || selectedDropdownItem instanceof DropdownItemGroup) {
        return [];
    }

    return [selectedDropdownItem];
});

/**
 * The selected text. It defaults to the caption if no item is selected
 */
const selectedText = computed(() => {
    if (props.keepCaption) {
        return props.caption ?? undefined;
    }

    return selectedItem.value?.text ?? props.caption ?? undefined;
});

/**
 * The selected icon. It defaults to the icon if no item is selected
 */
const selectedIcon = computed(() => {
    if (props.keepCaption) {
        return props.icon ?? undefined;
    }

    return selectedItem.value?.icon ?? props.icon ?? undefined;
});
</script>
